import { Controller } from "@hotwired/stimulus";
import {
  connectImmediateSortSync,
  disconnectImmediateSortSync,
  markTableFrameLoaded,
  matchesTurboFrameFetch,
  revertOptimisticSort,
  showTableSkeleton,
  SORT_LINK_SELECTOR,
} from "./table_frame_support";

const SHELL_BREAKPOINT = /-(desktop|tablet|mobile)$/;

function canonicalId(id) {
  const separator = id.lastIndexOf("__")
  return separator === -1 ? id : id.slice(separator + 2)
}

export default class extends Controller {
  static targets = ["frame", "skeletonTemplate", "sortStatus"];
  static values = {
    tableFrameId: {
      type: String,
      default: "educator-home-performance-trends-discipline-table",
    },
  };

  initialize() {
    this.scopeIdsToShell();
  }

  connect() {
    this.scopeIdsToShell();
    connectImmediateSortSync(this);
    this.boundOnTableFetchError = this.onTableFetchError.bind(this);
    this.boundMarkSortNavigation = this.markSortNavigation.bind(this);
    this.element.addEventListener("click", this.boundMarkSortNavigation, true);
    if (this.hasFrameTarget) {
      this.frameTarget.addEventListener(
        "turbo:fetch-request-error",
        this.boundOnTableFetchError,
      );
      this.frameTarget.addEventListener(
        "turbo:frame-missing",
        this.boundOnTableFetchError,
      );
    }
  }

  disconnect() {
    disconnectImmediateSortSync(this);
    if (this.sortAnnounceRafId) {
      cancelAnimationFrame(this.sortAnnounceRafId);
      this.sortAnnounceRafId = null;
    }
    this.element.removeEventListener(
      "click",
      this.boundMarkSortNavigation,
      true,
    );
    if (this.hasFrameTarget && this.boundOnTableFetchError) {
      this.frameTarget.removeEventListener(
        "turbo:fetch-request-error",
        this.boundOnTableFetchError,
      );
      this.frameTarget.removeEventListener(
        "turbo:frame-missing",
        this.boundOnTableFetchError,
      );
    }
  }

  onTableFetchError(event) {
    if (this.hasFrameTarget && event.target !== this.frameTarget) return;

    this.restoreTableHtml();
    revertOptimisticSort(this);
  }

  beforeTableFetch(event) {
    if (!matchesTurboFrameFetch(event, [this.tableFrameIdValue])) return;

    if (
      this.tableHtmlSnapshot == null &&
      this.hasFrameTarget &&
      this.frameTarget.getAttribute("aria-busy") !== "true"
    ) {
      this.captureTableHtml();
    }
    showTableSkeleton(this);
  }

  markSortNavigation(event) {
    if (!this.hasFrameTarget) return;

    const link = event.target.closest(SORT_LINK_SELECTOR);
    if (!link || !this.frameTarget.contains(link)) return;

    this.pendingSortAnnouncement = true;
  }

  tableLoaded() {
    this.clearTableHtml();
    markTableFrameLoaded(this);
    if (!this.pendingSortAnnouncement) return;

    this.pendingSortAnnouncement = false;
    this.announceLoadedSort();
  }

  scopeIdsToShell() {
    const outerFrame = this.element.closest("turbo-frame");
    const breakpoint = outerFrame?.id?.match(SHELL_BREAKPOINT)?.[1];
    if (!breakpoint) return;

    const baseTableFrameId = canonicalId(this.tableFrameIdValue).replace(
      SHELL_BREAKPOINT,
      "",
    );
    const scopedTableFrameId = `${baseTableFrameId}-${breakpoint}`;

    if (this.hasFrameTarget) this.frameTarget.id = scopedTableFrameId;
    this.tableFrameIdValue = scopedTableFrameId;

    this.element.querySelectorAll("[data-turbo-frame]").forEach((node) => {
      const current = node.getAttribute("data-turbo-frame");
      if (!current) return;
      if (canonicalId(current).replace(SHELL_BREAKPOINT, "") !== baseTableFrameId) return;

      node.setAttribute("data-turbo-frame", scopedTableFrameId);
    });

    this.element
      .querySelectorAll("[data-aquifer-ui-pagination-rows-frame-id-value]")
      .forEach((node) => {
        const current = node.getAttribute(
          "data-aquifer-ui-pagination-rows-frame-id-value",
        );
        if (!current) return;
        if (current.replace(SHELL_BREAKPOINT, "") !== baseTableFrameId) return;

        node.setAttribute(
          "data-aquifer-ui-pagination-rows-frame-id-value",
          scopedTableFrameId,
        );
      });

    this.element
      .querySelectorAll("table[id*='educator-home-performance-trends-']")
      .forEach((table) => {
        const nextId = `${canonicalId(table.id).replace(SHELL_BREAKPOINT, "")}-${breakpoint}`;
        if (nextId === table.id) return;

        const oldCaptionId = `${table.id}-caption`;
        const nextCaptionId = `${nextId}-caption`;
        this.element
          .querySelector(`[id="${oldCaptionId}"]`)
          ?.setAttribute("id", nextCaptionId);
        this.element
          .querySelectorAll(`[aria-labelledby="${oldCaptionId}"]`)
          .forEach((region) => {
            region.setAttribute("aria-labelledby", nextCaptionId);
          });
        table.id = nextId;
      });
  }

  captureTableHtml() {
    if (!this.hasFrameTarget) return;

    this.tableHtmlSnapshot = this.frameTarget.innerHTML;
  }

  restoreTableHtml() {
    if (!this.hasFrameTarget || this.tableHtmlSnapshot == null) return;

    this.frameTarget.innerHTML = this.tableHtmlSnapshot;
    this.clearTableHtml();
  }

  clearTableHtml() {
    delete this.tableHtmlSnapshot;
  }

  announceLoadedSort() {
    if (!this.hasFrameTarget) return;

    const activeHeader = this.frameTarget.querySelector(
      'th[aria-sort="ascending"], th[aria-sort="descending"]',
    );
    if (!activeHeader) return;

    const label = activeHeader.textContent.replace(/\s+/g, " ").trim();
    if (!label) return;

    const direction =
      activeHeader.getAttribute("aria-sort") === "descending"
        ? "descending"
        : "ascending";
    this.announceSort(label, direction);
  }

  announceSort(label, direction = "ascending") {
    if (!this.hasSortStatusTarget) return;

    const message = `Sorted by ${label}, ${direction}.`;
    if (this.sortStatusTarget.textContent === message) return;

    if (this.sortAnnounceRafId) cancelAnimationFrame(this.sortAnnounceRafId);
    this.sortStatusTarget.textContent = "";
    this.sortAnnounceRafId = requestAnimationFrame(() => {
      this.sortStatusTarget.textContent = message;
      this.sortAnnounceRafId = null;
    });
  }
}
